import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Upload, Download, FileSpreadsheet, Globe, Webhook, Facebook,
  Plus, Settings, Copy, Check, RefreshCw, Trash2, Edit2,
  ExternalLink, ChevronRight, AlertCircle, CheckCircle2,
  FileText, Table, ArrowRight
} from "lucide-react";
import api from "../api";

const LeadSourcesPage = () => {
  const [activeTab, setActiveTab] = useState("sources");
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  
  // Lead sources data
  const [sources, setSources] = useState([]);
  const [sourceStats, setSourceStats] = useState({});
  
  // Import state
  const [importSession, setImportSession] = useState(null);
  const [columnMappings, setColumnMappings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Dialogs
  const [showAddSourceDialog, setShowAddSourceDialog] = useState(false);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  
  // Form data
  const [sourceForm, setSourceForm] = useState({
    name: "",
    source_type: "webhook",
    default_status: "unqualified",
    auto_notify: true
  });

  const sourceTypeLabels = {
    manual: "Manual Entry",
    landing_page: "Landing Page",
    cold_call: "Cold Call",
    facebook_lead_ads: "Facebook Lead Ads",
    website_form: "Website Form",
    website_traffic: "Website Traffic",
    webhook: "Custom Webhook",
    linkedin: "LinkedIn",
    google_ads: "Google Ads",
    trade_show: "Trade Show",
    import: "CSV/Excel Import",
    referral: "Referral",
    other: "Other"
  };

  const sourceTypeIcons = {
    facebook_lead_ads: <Facebook className="h-4 w-4" />,
    webhook: <Webhook className="h-4 w-4" />,
    website_form: <Globe className="h-4 w-4" />,
    import: <FileSpreadsheet className="h-4 w-4" />,
    landing_page: <FileText className="h-4 w-4" />
  };

  // Fetch lead sources
  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/lead-sources");
      setSources(response.data.sources || []);
      setSourceStats(response.data.source_stats || {});
    } catch (error) {
      console.error("Error fetching lead sources:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Create lead source
  const handleCreateSource = async () => {
    try {
      const response = await api.post("/lead-sources", sourceForm);
      toast.success("Lead source created");
      setSources([...sources, response.data]);
      setShowAddSourceDialog(false);
      setSourceForm({ name: "", source_type: "webhook", default_status: "unqualified", auto_notify: true });
      
      // Show webhook URL if it's a webhook source
      if (response.data.source_type === "webhook" || response.data.source_type === "facebook_lead_ads") {
        setSelectedSource(response.data);
        setShowWebhookDialog(true);
      }
    } catch (error) {
      toast.error("Failed to create lead source");
    }
  };

  // Delete lead source
  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm("Are you sure you want to delete this lead source?")) return;
    try {
      await api.delete(`/lead-sources/${sourceId}`);
      toast.success("Lead source deleted");
      setSources(sources.filter(s => s.id !== sourceId));
    } catch (error) {
      toast.error("Failed to delete lead source");
    }
  };

  // Toggle source active
  const handleToggleSource = async (source) => {
    try {
      await api.put(`/lead-sources/${source.id}`, { is_active: !source.is_active });
      setSources(sources.map(s => s.id === source.id ? {...s, is_active: !s.is_active} : s));
      toast.success(`Source ${source.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error("Failed to update source");
    }
  };

  // Copy webhook URL
  const copyWebhookUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
    toast.success("Webhook URL copied!");
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error("Please upload a CSV or Excel file");
      return;
    }

    // Check file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setImporting(true);
      const response = await api.post("/import/cold-calls/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setImportSession(response.data);
      setColumnMappings(response.data.suggested_mappings || []);
      toast.success(`File uploaded: ${response.data.row_count} rows found`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setImporting(false);
    }
  };

  // Update column mapping
  const updateMapping = (fileColumn, dbField) => {
    const existing = columnMappings.find(m => m.file_column === fileColumn);
    if (existing) {
      setColumnMappings(columnMappings.map(m => 
        m.file_column === fileColumn ? {...m, db_field: dbField} : m
      ));
    } else {
      setColumnMappings([...columnMappings, { file_column: fileColumn, db_field: dbField }]);
    }
  };

  // Execute import
  const executeImport = async () => {
    if (!importSession) return;
    
    // Check required mapping
    const hasCompanyName = columnMappings.some(m => m.db_field === "company_name");
    if (!hasCompanyName) {
      toast.error("Company Name mapping is required");
      return;
    }

    try {
      setImporting(true);
      const response = await api.post(`/import/cold-calls/${importSession.import_id}/execute`, {
        mappings: columnMappings,
        skip_header: true,
        source_name: `Import: ${importSession.filename}`,
        default_status: "new",
        tags: ["imported"]
      });
      
      setImportResult(response.data);
      toast.success(`Imported ${response.data.imported_count} prospects`);
      setImportSession(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Cancel import
  const cancelImport = async () => {
    if (importSession?.import_id) {
      await api.delete(`/import/cold-calls/${importSession.import_id}`);
    }
    setImportSession(null);
    setColumnMappings([]);
    setImportResult(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-sources-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Lead Sources & Import</h1>
          <p className="text-muted-foreground">Configure lead sources and import prospects from files</p>
        </div>
        <Button onClick={() => setShowAddSourceDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Lead Source
        </Button>
      </div>

      {/* Source Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(sourceStats).map(([type, count]) => (
          <Card key={type} className="bg-slate-50">
            <CardContent className="pt-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                {sourceTypeIcons[type] || <Globe className="h-4 w-4" />}
                <span className="text-2xl font-bold">{count}</span>
              </div>
              <p className="text-xs text-muted-foreground">{sourceTypeLabels[type] || type}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sources">Lead Sources</TabsTrigger>
          <TabsTrigger value="import">Import from File</TabsTrigger>
          <TabsTrigger value="webhooks">Webhook Setup</TabsTrigger>
        </TabsList>

        {/* Lead Sources Tab */}
        <TabsContent value="sources" className="space-y-4">
          <div className="grid gap-4">
            {sources.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-semibold">No Lead Sources Configured</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set up lead sources to track where your leads come from
                  </p>
                  <Button onClick={() => setShowAddSourceDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Lead Source
                  </Button>
                </CardContent>
              </Card>
            ) : (
              sources.map((source) => (
                <Card key={source.id} className={!source.is_active ? "opacity-60" : ""}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${source.is_active ? 'bg-blue-100' : 'bg-slate-100'}`}>
                          {sourceTypeIcons[source.source_type] || <Globe className="h-5 w-5" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{source.name}</h3>
                            <Badge variant="outline">{sourceTypeLabels[source.source_type]}</Badge>
                            {!source.is_active && <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Default status: {source.default_status} • {source.lead_count || 0} leads received
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(source.source_type === "webhook" || source.source_type === "facebook_lead_ads") && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedSource(source); setShowWebhookDialog(true); }}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Webhook
                          </Button>
                        )}
                        <Switch 
                          checked={source.is_active}
                          onCheckedChange={() => handleToggleSource(source)}
                        />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteSource(source.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-4">
          {!importSession ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Import Cold Call Prospects
                </CardTitle>
                <CardDescription>
                  Upload a CSV or Excel file to import prospects into your cold calling list
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-slate-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">Drop your file here or click to browse</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Supports CSV, XLSX files up to 10MB (~50,000 rows)
                  </p>
                  <Button variant="outline">
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {importResult && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Import Complete</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Successfully imported {importResult.imported_count} prospects.
                      {importResult.error_count > 0 && ` ${importResult.error_count} errors.`}
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => setImportResult(null)}
                    >
                      Import Another File
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Table className="h-5 w-5" />
                  Map Columns: {importSession.filename}
                </CardTitle>
                <CardDescription>
                  {importSession.row_count} rows found. Map your file columns to the correct fields.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Column Mapping */}
                <div className="space-y-3 mb-6">
                  <Label className="font-medium">Column Mapping</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {importSession.headers.map((header) => (
                      <div key={header} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="text-sm font-medium min-w-[120px]">{header}</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <Select
                          value={columnMappings.find(m => m.file_column === header)?.db_field || "skip"}
                          onValueChange={(v) => updateMapping(header, v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">-- Skip --</SelectItem>
                            {importSession.available_fields.map((f) => (
                              <SelectItem key={f.field} value={f.field}>
                                {f.label} {f.required && "*"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                {importSession.preview_data?.length > 0 && (
                  <div className="mb-6">
                    <Label className="font-medium mb-2 block">Preview (first 5 rows)</Label>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border">
                        <thead className="bg-slate-100">
                          <tr>
                            {importSession.headers.map((h) => (
                              <th key={h} className="p-2 text-left border">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importSession.preview_data.map((row, idx) => (
                            <tr key={idx} className="border-t">
                              {row.map((cell, cellIdx) => (
                                <td key={cellIdx} className="p-2 border">{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button onClick={executeImport} disabled={importing}>
                    {importing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Import {importSession.row_count} Rows
                  </Button>
                  <Button variant="outline" onClick={cancelImport}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhook Integration Guide
              </CardTitle>
              <CardDescription>
                Receive leads automatically from external platforms
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Facebook Lead Ads */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold">Facebook Lead Ads</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Automatically capture leads from your Facebook Lead Ad campaigns.
                </p>
                <ol className="text-sm space-y-2 mb-4">
                  <li>1. Create a &quot;Facebook Lead Ads&quot; source above</li>
                  <li>2. Copy the webhook URL</li>
                  <li>3. In Facebook Business Manager, go to Integrations → Lead Access</li>
                  <li>4. Add the webhook URL and subscribe to your page</li>
                </ol>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSourceForm({...sourceForm, name: "Facebook Lead Ads", source_type: "facebook_lead_ads"});
                    setShowAddSourceDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Facebook Source
                </Button>
              </div>

              {/* Custom Webhook */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Webhook className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold">Custom Webhook</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Integrate with any platform that supports webhooks (Typeform, HubSpot, Zapier, etc.)
                </p>
                <div className="bg-slate-50 p-3 rounded text-sm font-mono mb-3">
                  <p className="text-muted-foreground mb-2">POST request with JSON body:</p>
                  <pre className="text-xs overflow-x-auto">
{`{
  "first_name": "John",
  "last_name": "Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "company": "Acme Inc",
  "city": "Toronto",
  "state": "Ontario",
  "country": "Canada"
}`}
                  </pre>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSourceForm({...sourceForm, name: "Custom Integration", source_type: "webhook"});
                    setShowAddSourceDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Webhook Source
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Source Dialog */}
      <Dialog open={showAddSourceDialog} onOpenChange={setShowAddSourceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Lead Source</DialogTitle>
            <DialogDescription>
              Configure a new source for incoming leads
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Source Name *</Label>
              <Input
                value={sourceForm.name}
                onChange={(e) => setSourceForm({...sourceForm, name: e.target.value})}
                placeholder="e.g., Facebook Ads, Website Contact Form"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Source Type *</Label>
              <Select
                value={sourceForm.source_type}
                onValueChange={(v) => setSourceForm({...sourceForm, source_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook_lead_ads">Facebook Lead Ads</SelectItem>
                  <SelectItem value="website_form">Website Form</SelectItem>
                  <SelectItem value="webhook">Custom Webhook</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="google_ads">Google Ads</SelectItem>
                  <SelectItem value="trade_show">Trade Show</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Default Lead Status</Label>
              <Select
                value={sourceForm.default_status}
                onValueChange={(v) => setSourceForm({...sourceForm, default_status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="unqualified">Unqualified</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Notify on new lead</Label>
              <Switch
                checked={sourceForm.auto_notify}
                onCheckedChange={(v) => setSourceForm({...sourceForm, auto_notify: v})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSourceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSource} disabled={!sourceForm.name}>Create Source</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook URL Dialog */}
      <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook Configuration</DialogTitle>
            <DialogDescription>
              Use this URL to receive leads from external platforms
            </DialogDescription>
          </DialogHeader>
          
          {selectedSource && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={`${window.location.origin}${selectedSource.webhook_url}`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyWebhookUrl(selectedSource.webhook_url)}
                  >
                    {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              {selectedSource.source_type === "facebook_lead_ads" && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  <p className="font-medium text-blue-700 mb-2">Facebook Setup Instructions:</p>
                  <ol className="text-blue-600 space-y-1">
                    <li>1. Go to Facebook Business Settings</li>
                    <li>2. Navigate to Integrations → Lead Access</li>
                    <li>3. Click &quot;CRMs&quot; and add a new integration</li>
                    <li>4. Paste the webhook URL above</li>
                    <li>5. Subscribe to your page&apos;s lead forms</li>
                  </ol>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowWebhookDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadSourcesPage;
